import React from 'react';
import { Box, Button, TextField, Typography, Grid, Paper, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCreateRiskAssessmentMutation } from '../../features/schedule/riskAssessmentSlice';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// 導入新的模組化組件和 hooks
import { useRiskAssessment } from './hooks/useRiskAssessment';
import { RiskAssessmentTable } from './components/RiskAssessmentTable';
import { SafetyPrecautionsList } from './components/SafetyPrecautionsList';
import { RiskRatingTable } from './components/RiskRatingTable';
import { SignatureSection } from './components/SignatureSection';
import { calculateRiskRating } from './utils/riskAssessmentUtils';

const RiskAssessmentForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [createRiskAssessment] = useCreateRiskAssessmentMutation();

    // 從路由狀態獲取參數
    const { project, position, entry } = location.state || {};

    // 使用自定義 Hook 管理狀態
    const {
        site, setSite,
        workContent, setWorkContent,
        contractor, setContractor,
        subcontractor, setSubcontractor,
        competentPerson, setCompetentPerson,
        assessmentSignature, setAssessmentSignature,
        reportReceiptSignature, setReportReceiptSignature,
        riskAssessmentItems, setRiskAssessmentItems,
        safetyPrecautionsChecklist, setSafetyPrecautionsChecklist,
        currentMode, isViewMode, scheduleID,
        isLoading, error,
        handleDownloadReport
    } = useRiskAssessment();



    // 處理風險評估項目變更
    const handleRiskAssessmentChange = (index: number, field: keyof any, value: any, subIndex?: number, subField?: string) => {
        const newItems = [...riskAssessmentItems];

        if (field === 'assessmentResult') {
            newItems[index].assessmentResult = { ...newItems[index].assessmentResult, ...value };
        } else if (field === 'defaultSafetyPrecautions' && subIndex !== undefined) {
            if (newItems[index].defaultSafetyPrecautions && newItems[index].defaultSafetyPrecautions[subIndex]) {
                newItems[index].defaultSafetyPrecautions[subIndex].isChecked = value;
            }
        } else if (field === 'extraSafetyPrecautions' && subIndex !== undefined) {
            if (newItems[index].extraSafetyPrecautions && newItems[index].extraSafetyPrecautions[subIndex]) {
                newItems[index].extraSafetyPrecautions[subIndex].content = value;
            }
        } else if (field === 'stayHour') {
            newItems[index].stayHour = Number(value);
        } else {
            (newItems[index] as any)[field] = value;
        }

        // 重新計算風險評級
        if (field === 'severity' || field === 'likelihood') {
            const item = newItems[index];
            item.riskRating = calculateRiskRating(item.severity, item.likelihood);
        }

        setRiskAssessmentItems(newItems);
    };

    // 添加新的安全預防措施
    const handleAddSafetyPrecaution = (index: number) => {
        const newItems = [...riskAssessmentItems];
        if (!newItems[index].extraSafetyPrecautions) {
            newItems[index].extraSafetyPrecautions = [];
        }

        newItems[index].extraSafetyPrecautions.push({
            item: `extra_precaution_${Date.now()}`,
            content: ''
        });

        setRiskAssessmentItems(newItems);
    };

    // 更新安全預防措施內容
    const handleUpdateSafetyPrecautionContent = (index: number, subIndex: number, content: string, type: 'default' | 'extra') => {
        const newItems = [...riskAssessmentItems];
        if (type === 'default' && newItems[index].defaultSafetyPrecautions && newItems[index].defaultSafetyPrecautions[subIndex]) {
            newItems[index].defaultSafetyPrecautions[subIndex].content = content;
        } else if (type === 'extra' && newItems[index].extraSafetyPrecautions && newItems[index].extraSafetyPrecautions[subIndex]) {
            newItems[index].extraSafetyPrecautions[subIndex].content = content;
        }
        setRiskAssessmentItems(newItems);
    };

    // 刪除安全預防措施
    const handleDeleteSafetyPrecaution = (index: number, subIndex: number, type: 'default' | 'extra') => {
        const newItems = [...riskAssessmentItems];
        if (type === 'default' && newItems[index].defaultSafetyPrecautions && newItems[index].defaultSafetyPrecautions[subIndex]) {
            newItems[index].defaultSafetyPrecautions.splice(subIndex, 1);
        } else if (type === 'extra' && newItems[index].extraSafetyPrecautions && newItems[index].extraSafetyPrecautions[subIndex]) {
            newItems[index].extraSafetyPrecautions.splice(subIndex, 1);
        }
        setRiskAssessmentItems(newItems);
    };

    // 處理安全預防措施清單變更
    const handleSafetyPrecautionsChange = (itemKey: string, value: boolean | string) => {
        setSafetyPrecautionsChecklist(prev => {
            const newState = { ...prev };

            if (itemKey === 'item7.main') {
                newState.item7 = { ...newState.item7, main: value as boolean };
            } else if (itemKey === 'item7.oxygen') {
                newState.item7 = { ...newState.item7, oxygen: value as boolean };
            } else if (itemKey === 'item7.explosiveLimit') {
                newState.item7 = { ...newState.item7, explosiveLimit: value as boolean };
            } else if (itemKey === 'item7.hydrogenSulfide') {
                newState.item7 = { ...newState.item7, hydrogenSulfide: value as boolean };
            } else if (itemKey === 'item7.carbonMonoxide') {
                newState.item7 = { ...newState.item7, carbonMonoxide: value as boolean };
            } else if (itemKey === 'item7.other') {
                newState.item7 = { ...newState.item7, other: value as boolean };
            } else if (itemKey === 'item7.otherText') {
                newState.item7 = { ...newState.item7, otherText: value as string };
            } else if (itemKey === 'item17.main') {
                newState.item17 = { ...newState.item17, main: value as boolean };
            } else if (itemKey === 'item17.other') {
                newState.item17 = { ...newState.item17, other: value as string };
            } else {
                (newState as any)[itemKey] = value;
            }

            return newState;
        });
    };



    // 儲存
    const handleSave = async () => {
        if (isViewMode) return;

        const data = {
            scheduleID,
            site,
            workContent,
            contractor,
            subcontractor,
            competentPerson,
            riskAssessmentItems,
            safetyPrecautionsChecklist,
            assessmentSignature,
            reportReceiptSignature
        };

        try {
            const result = await createRiskAssessment(data).unwrap();
            console.log('保存成功:', result);
            alert('送出成功！');
            navigate('/add-work-schedule', {
                state: {
                    project: project,
                    position: position,
                    entry: entry
                }
            });
        } catch (error) {
            console.error('保存失敗:', error);
            alert('送出失敗，請稍後再試');
        }
    };

    const handleClose = () => {
        !isViewMode ? navigate('/add-work-schedule', {
            state: {
                project: project,
                position: position,
                entry: entry
            }
        }) : navigate('/entry-detail', {
            state: {
                project: project,
                position: position,
                entry: entry
            }
        });
    };

    // 顯示載入狀態
    if (isLoading) {
        return (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <Typography variant="h6">載入中...</Typography>
            </Box>
        );
    }

    // 顯示錯誤狀態
    if (error) {
        return (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                    載入失敗
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    無法載入風險評估數據，請稍後再試
                </Typography>
                <Button variant="outlined" onClick={handleClose}>
                    返回
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* 頂部標題和操作區域 */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            mr: 2,
                            backgroundColor: '#f4a460',
                            color: '#fff',
                            '&:hover': {
                                backgroundColor: '#e69450',
                            },
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                </Box>
                <Typography variant="h5" sx={{ textAlign: 'center', flex: 1 }}>
                    {currentMode === 'create' ? t('Create Risk Assessment') : currentMode === 'edit' ? t('Edit Risk Assessment') : t('View Risk Assessment')}
                    {/* {scheduleID && ` (ID: ${scheduleID})`} */}
                </Typography>
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    {isViewMode && (
                        <Button
                            variant="contained"
                            onClick={handleDownloadReport}
                            startIcon={<DownloadIcon />}
                            sx={{
                                backgroundColor: '#f4a460',
                                color: '#fff',
                                fontWeight: 'bold',
                                borderRadius: '8px',
                                padding: '8px 16px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                '&:hover': {
                                    backgroundColor: '#e69450',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                                    transform: 'translateY(-1px)'
                                },
                                '&:active': {
                                    transform: 'translateY(0)'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            {t('Download PDF')}
                        </Button>
                    )}
                </Box>
            </Box>

            {/* 基本資料區 */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ background: '#f4a460', p: 1, borderRadius: 1, mb: 2 }}>
                    {t('Basic Information')}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label={t('Workplace')}
                            value={site}
                            onChange={e => setSite(e.target.value)}
                            fullWidth
                            disabled={isViewMode}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label={t('Work Content')}
                            value={workContent}
                            onChange={e => setWorkContent(e.target.value)}
                            fullWidth
                            disabled={isViewMode}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label={t('Contractor/Owner')}
                            value={contractor}
                            onChange={e => setContractor(e.target.value)}
                            fullWidth
                            disabled={isViewMode}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label={t('Subcontractor (if applicable)')}
                            value={subcontractor}
                            onChange={e => setSubcontractor(e.target.value)}
                            fullWidth
                            disabled={isViewMode}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* 合資格人士資料 */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ background: '#f4a460', p: 1, borderRadius: 1, mb: 2 }}>
                    {t('Competent Person Information')}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label={t('Competent Person Name')}
                            value={competentPerson.name}
                            onChange={e => setCompetentPerson(prev => ({ ...prev, name: e.target.value }))}
                            fullWidth
                            disabled={isViewMode}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label={t('Certificate Number')}
                            value={competentPerson.certificateNumber}
                            onChange={e => setCompetentPerson(prev => ({ ...prev, certificateNumber: e.target.value }))}
                            fullWidth
                            disabled={isViewMode}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label={t('Certificate Expiry Date')}
                            type="date"
                            value={competentPerson.expiryDate}
                            onChange={e => setCompetentPerson(prev => ({ ...prev, expiryDate: e.target.value }))}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            disabled={isViewMode}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* 危險評估內容表格 */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ background: '#f4a460', p: 1, borderRadius: 1, mb: 2 }}>
                    {t('Risk Assessment Content')}
                </Typography>
                <RiskAssessmentTable
                    riskAssessmentItems={riskAssessmentItems}
                    isViewMode={isViewMode}
                    onRiskAssessmentChange={handleRiskAssessmentChange}
                    onAddSafetyPrecaution={handleAddSafetyPrecaution}
                    onUpdateSafetyPrecautionContent={handleUpdateSafetyPrecautionContent}
                    onDeleteSafetyPrecaution={handleDeleteSafetyPrecaution}
                />
            </Paper>

            {/* 頁腳說明 */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    *{t('Risk Rating Table Reference')}
                </Typography>
            </Paper>

            {/* 安全預防措施清單 */}
            <SafetyPrecautionsList
                safetyPrecautionsChecklist={safetyPrecautionsChecklist}
                isViewMode={isViewMode}
                onSafetyPrecautionsChange={handleSafetyPrecautionsChange}
            />

            {/* 危險評級表 */}
            <RiskRatingTable />

            {/* 簽名區域 */}
            <SignatureSection
                assessmentSignature={assessmentSignature}
                reportReceiptSignature={reportReceiptSignature}
                isViewMode={isViewMode}
                onAssessmentSignatureChange={(field, value) =>
                    setAssessmentSignature(prev => ({ ...prev, [field]: value }))
                }
                onReportReceiptSignatureChange={(field, value) =>
                    setReportReceiptSignature(prev => ({ ...prev, [field]: value }))
                }
            />

            {/* 按鈕區域 */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                {!isViewMode && <Button variant="contained" color="primary" onClick={handleSave}>{t('Save')}</Button>}
                <Button variant="outlined" color="secondary" onClick={handleClose}>
                    {isViewMode ? t('Close') : t('Cancel')}
                </Button>
            </Box>
        </Box>
    );
};

export default RiskAssessmentForm;